/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * Domain page views after the service-console reference: DNSSEC at a glance (24) and WHOIS
 * privacy with the public record preview (25). Both read existing data only.
 */

import { KeyRound, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api, type DomainDetail } from '../../../api';
import type { RdapPreviewField } from '../../../api/areas/market';
import { Badge, type Tone } from '../../../components/Badge';
import { Callout, DataTable, Section, StatCard, StatGrid, type Column } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { FactRows, GateList, TwoColumn } from '../../../components/flow';
import { EmptyState, Loading } from '../../../components/States';
import { formatDateTime } from '../../../lib/format';
import { useApi } from '../../../lib/useApi';
import { hubUrl } from '../../../nav/hubs';
import { DIGEST_TYPES, DNSSEC_ALGORITHMS } from './CoreTabs';
import { domainTabUrl } from './moved';
import { policyLabel } from '@/lib/labels';

/** Reference 24: DS records at the registry, their keys and what the console does not check. */
export function DnssecSummary({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('ux');
  const d = detail.domain;
  const del = useApi((signal) => api.getDomainDelegation(d.id, { signal }), [d.id]);
  if (del.status !== 'success') return del.status === 'error' ? <DataError error={del.error} onRetry={del.reload} title={t('dnssec.loadError')} /> : <Loading rows={2} />;
  const ds = del.data.ds_records;
  const algos = [...new Set(ds.map((x) => DNSSEC_ALGORITHMS[x.algorithm] ?? String(x.algorithm)))];
  const digests = [...new Set(ds.map((x) => DIGEST_TYPES[x.digest_type] ?? String(x.digest_type)))];
  return (
    <div className="space-y-5">
      <StatGrid>
        <StatCard label={t('dnssec.state')} icon={ShieldCheck} tone={ds.length ? 'ok' : 'neutral'} value={ds.length ? t('dnssec.signed') : t('dnssec.unsigned')} hint={t('dnssec.stateHint')} />
        <StatCard label={t('dnssec.ds')} icon={KeyRound} value={ds.length} hint={del.data.registry_name} />
        <StatCard label={t('dnssec.keyTags')} value={ds.length ? ds.map((x) => x.key_tag).join(', ') : '—'} hint={algos.join(' · ') || t('dnssec.noKeys')} />
        <StatCard label={t('dnssec.digest')} value={digests.join(' · ') || '—'} hint={ds.length ? formatDateTime(ds.map((x) => x.updated_at).sort().at(-1)) : t('dnssec.noKeys')} />
      </StatGrid>
      <Section title={t('dnssec.chain')} description={t('dnssec.chainHint')}>
        <GateList
          gates={[
            { id: 'tld', title: t('dnssec.gate.tld'), hint: del.data.supports_dnssec === false ? t('dnssec.gate.tldNo') : undefined, ok: del.data.supports_dnssec },
            { id: 'ds', title: t('dnssec.gate.ds'), hint: ds.length ? t('dnssec.gate.dsHint', { n: ds.length }) : t('dnssec.gate.dsNone'), ok: ds.length > 0 },
            { id: 'keys', title: t('dnssec.gate.keys'), hint: t('dnssec.gate.keysHint'), ok: null },
          ]}
        />
      </Section>
    </div>
  );
}

const SHOWN_TONE: Record<RdapPreviewField['shown'], Tone> = { PUBLIC: 'ok', REDACTED: 'warn', CONTACT_FORM: 'info', OMITTED: 'neutral' };

/** Reference 25: the WHOIS privacy add-on of this domain and what RDAP / WHOIS publish for it. */
export function PrivacyView({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('ux');
  const { t: tc } = useTranslation('domains');
  const d = detail.domain;
  const services = useApi((signal) => api.getDomainServices(d.id, { signal }), [d.id]);
  const preview = useApi((signal) => api.previewRdap(d.ascii_name, { signal }), [d.ascii_name]);
  const privacy = services.status === 'success' ? services.data.items.find((a) => a.addon_code === 'WHOIS_PRIVACY' && (a.state === 'ACTIVE' || a.state === 'ORDERED')) : undefined;
  const offered = services.status === 'success' ? services.data.available.find((a) => a.code === 'WHOIS_PRIVACY') : undefined;
  const cols: Column<RdapPreviewField>[] = [
    { key: 'field', header: t('privacy.col.field'), cell: (f) => <span className="font-mono text-xs">{f.field}</span> },
    { key: 'who', header: t('privacy.col.who'), cell: (f) => (f.registrant ? t('privacy.registrant') : t('privacy.other')) },
    { key: 'shown', header: t('privacy.col.shown'), cell: (f) => <Badge tone={SHOWN_TONE[f.shown]}>{t(`privacy.shown.${f.shown}`)}</Badge> },
    { key: 'value', header: t('privacy.col.value'), className: 'max-w-72 whitespace-normal break-words text-xs', cell: (f) =>
        f.value === null || f.value === undefined ? '—' : typeof f.value === 'boolean' ? (f.value ? tc('common.yes') : tc('common.no')) : typeof f.value === 'string' ? f.value : JSON.stringify(f.value),
    },
  ];
  return (
    <TwoColumn
      main={
        <>
          <Section title={t('privacy.serviceTitle')} description={t('privacy.serviceHint')}>
            {services.status === 'loading' ? <Loading rows={1} /> : null}
            {services.status === 'error' ? <DataError error={services.error} onRetry={services.reload} title={t('privacy.loadError')} /> : null}
            {services.status === 'success' ? (
              <FactRows
                rows={[
                  [t('privacy.state'), privacy ? <Badge key="s" tone={privacy.state === 'ACTIVE' ? 'ok' : 'pending'}>{t(`privacy.addon.${privacy.state}`)}</Badge> : offered ? t('privacy.notUsed') : t('privacy.notOffered')],
                  !!privacy && [t('privacy.period'), privacy.period_start ? `${formatDateTime(privacy.period_start)} → ${privacy.period_end ? formatDateTime(privacy.period_end) : '…'}` : '—'],
                  !privacy && !!offered && [t('privacy.orderable'), offered.orderable ? t('privacy.yes') : t('privacy.noPrice')],
                ]}
              />
            ) : null}
            <Link to={domainTabUrl(d.ascii_name, 'addons')} className="mt-3 inline-block text-sm font-medium text-primary hover:underline">
              {t('privacy.manage')}
            </Link>
          </Section>
          <Section flush title={t('privacy.previewTitle')} description={t('privacy.previewHint')}>
            {preview.status === 'loading' ? (
              <div className="px-5 pb-5">
                <Loading rows={3} />
              </div>
            ) : null}
            {preview.status === 'error' ? (
              <div className="px-5 pb-5">
                <DataError error={preview.error} onRetry={preview.reload} title={t('privacy.loadError')} />
              </div>
            ) : null}
            {preview.status === 'success' ? (
              preview.data.found ? (
                <>
                  <p className="px-5 pb-3 text-xs text-muted-foreground">
                    {preview.data.policy ? t('privacy.policy', { key: policyLabel(preview.data.policy.policy_key), version: preview.data.policy.version }) : t('privacy.noPolicy')}
                  </p>
                  <DataTable columns={cols} rows={preview.data.fields} rowKey={(f) => f.field} />
                </>
              ) : (
                <EmptyState title={t('privacy.notPublished')} />
              )
            ) : null}
          </Section>
        </>
      }
      side={
        <>
          <Section title={t('privacy.twoTitle')}>
            <FactRows
              rows={[
                [t('privacy.internal'), t('privacy.internalHint')],
                [t('privacy.public'), t('privacy.publicHint')],
              ]}
            />
          </Section>
          <Callout tone="warn" title={t('privacy.notSameTitle')}>
            {t('privacy.notSame')}
          </Callout>
          <Section title={t('privacy.moreTitle')}>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to={hubUrl('compliance', 'icann', 'rdap')} className="text-primary hover:underline">
                  {t('privacy.linkPolicy')}
                </Link>
              </li>
              <li>
                <Link to={hubUrl('compliance', 'icann', 'disclosure')} className="text-primary hover:underline">
                  {t('privacy.linkDisclosure')}
                </Link>
              </li>
            </ul>
          </Section>
        </>
      }
    />
  );
}
